import { useState } from "react";
import type { ReactNode } from "react";
import type { FileResponse } from "@patent-studio/api-types";
import { FileText, Maximize2, Minimize2, X } from "lucide-react";
import { Button, IconButton } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import {
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { documentFormatLabel } from "@/lib/constants";
import { plural } from "@/lib/plural";
import type { HighlightTone } from "./HighlightLayer";
import { pageReference } from "./OpenDocument";
import type { PlacedPassage } from "./DocumentDrawer";

/** What the drawer knows of the document it is turned to. */
export interface Turned {
  file: FileResponse;
  tone: HighlightTone | null;
  /** The record's label for the document, where the route answered one. */
  label: string | null;
  passages: readonly PlacedPassage[];
  /** The page count as best known: the record's, then the renderer's, then the file's. */
  pages: number | null;
}

/**
 * "<label> · <title>", with what is not known left out. The label is the
 * record's where the route answered one, the D-number for prior art, and the
 * file's own name failing both; the title is what the record stamped on the file.
 */
function titleOf(turned: Turned): string {
  return [
    turned.label ?? turned.file.reference_id ?? turned.file.original_filename,
    turned.file.title,
  ]
    .filter((part) => part !== null && part !== "")
    .join(" · ");
}

/** The line under the title: the publication number, the page shown, the format and the page count. */
function detailOf(turned: Turned, page: number | null): string {
  return [
    turned.file.publication_number,
    page === null ? null : pageReference(page),
    documentFormatLabel(
      turned.file.content_type,
      turned.file.original_filename,
    ),
    turned.pages === null ? null : plural(turned.pages, "page"),
  ]
    .filter((part) => part !== null && part !== "")
    .join(" · ");
}

/**
 * The sheet as v0.29's document panel: 620 px from the right, or the whole
 * viewport at full screen, headed by the document's icon, its title over its
 * detail line, Full screen and a boxed close.
 */
export function DocumentPanel({
  turned,
  page,
  children,
}: {
  turned: Turned;
  /** The page the walk stands on, or null where no passage is shown. */
  page: number | null;
  children: ReactNode;
}) {
  const [fullscreen, setFullscreen] = useState(false);
  return (
    <SheetContent
      side="right"
      closeButton={false}
      data-fullscreen={fullscreen ? "true" : "false"}
      className={cn(
        "flex max-w-none flex-col gap-3 p-0 pb-5",
        fullscreen ? "w-screen" : "w-[min(620px,94vw)]",
      )}
    >
      <div
        data-testid="document-header"
        className="flex items-center gap-2.5 border-b border-border px-[18px] py-4"
      >
        <IconTile size="sm">
          <FileText />
        </IconTile>
        <div className="min-w-0 flex-1">
          <SheetTitle className="text-[13px] font-bold">
            {titleOf(turned)}
          </SheetTitle>
          <p
            data-testid="document-detail"
            className="mt-0.5 text-[11px] font-medium text-muted-foreground"
          >
            {detailOf(turned, page)}
          </p>
        </div>
        <Button
          variant="secondary"
          size="dense"
          onClick={() => {
            setFullscreen((f) => !f);
          }}
        >
          {fullscreen ? (
            <>
              <Minimize2 /> Exit full screen
            </>
          ) : (
            <>
              <Maximize2 /> Full screen
            </>
          )}
        </Button>
        <SheetClose asChild>
          <IconButton variant="secondary" aria-label="Close" title="Close">
            <X />
          </IconButton>
        </SheetClose>
      </div>
      <SheetDescription className="sr-only">
        The source document, opened at what the element cites.
      </SheetDescription>
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5">{children}</div>
    </SheetContent>
  );
}
